import { useSelector } from 'react-redux';
import {
  selectIsAdmin,
  selectCurrentProjectRole,
  selectCanManageSharedConfigs,
  selectCanManageSharedFields,
  selectPermissionsLoaded,
} from '../views/Project/projectSlice';

/**
 * Resources accessible by 'user' role (read-only)
 */
const USER_ACCESSIBLE_RESOURCES = ['tasks', 'executions', 'sequences'];

/**
 * React hook for permission checks
 * @returns {Object} Permission helper functions
 */
export function usePermission() {
  const isAdmin = useSelector(selectIsAdmin);
  const currentProjectRole = useSelector(selectCurrentProjectRole);
  const sharedConfigsFlag = useSelector(selectCanManageSharedConfigs);
  const sharedFieldsFlag = useSelector(selectCanManageSharedFields);
  const permissionsLoaded = useSelector(selectPermissionsLoaded);

  /**
   * Check if user is a global admin (is_admin: true)
   */
  const isGlobalAdmin = () => {
    return isAdmin === true;
  };

  /**
   * Check if user is an admin for the current project
   */
  const isProjectAdmin = () => {
    return currentProjectRole === 'admin';
  };

  /**
   * Check if user can edit (create/update/delete) in current project
   * True if global admin OR project admin
   */
  const canEdit = () => {
    return isGlobalAdmin() || isProjectAdmin();
  };

  /**
   * Check if user can access a specific resource
   * @param {string} resource - Resource name (e.g., 'tasks', 'executions', 'sequences')
   * @returns {boolean}
   */
  const canAccessResource = (resource) => {
    // Global admin can access everything
    if (isGlobalAdmin()) {
      return true;
    }

    // Project admin can access all project resources
    if (isProjectAdmin()) {
      return true;
    }

    // User role can only access specific resources
    if (currentProjectRole === 'user') {
      return USER_ACCESSIBLE_RESOURCES.includes(resource.toLowerCase());
    }

    // No access if no role
    return false;
  };

  /**
   * Check if user can view and manage shared configs.
   * The single can_manage_shared_configs flag gates both read and write, so there is
   * no separate view check. Global admins have it implicitly.
   */
  const canManageSharedConfigs = () => {
    return isGlobalAdmin() || sharedConfigsFlag === true;
  };

  /**
   * Check if user can view and manage shared fields.
   * The single can_manage_shared_fields flag gates both read and write, so there is
   * no separate view check. Global admins have it implicitly.
   */
  const canManageSharedFields = () => {
    return isGlobalAdmin() || sharedFieldsFlag === true;
  };

  return {
    isGlobalAdmin,
    isProjectAdmin,
    canEdit,
    canAccessResource,
    canManageSharedConfigs,
    canManageSharedFields,
    // State, not a check: false until /current-user answers.
    permissionsLoaded,
  };
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use usePermission() hook instead
 */
export function permissionValidator(action, resource) {
  // This function is kept for backward compatibility but should not be used
  // It always returns false as the old permission model is deprecated
  console.warn(
    'permissionValidator is deprecated. Use usePermission() hook instead.',
  );
  return false;
}
